import type { Moment } from 'moment-timezone';

import { moment, TimeFormat } from '@feathr/hooks';

const FIVE_MINUTES_MS = 5 * 60 * 1000;

/**
 * Sets `target`'s time of day to `source`'s hour and minute, zeroing seconds and milliseconds.
 * Mailrail send times are whole minutes, so sub-minute components are always zeroed to keep
 * schedule dates serializing and comparing consistently. Mutates and returns `target`.
 */
export function copyRoundedTimeOfDay(target: Moment, source: Moment): Moment {
  return target.set({
    hour: source.hour(),
    minute: source.minute(),
    second: 0,
    millisecond: 0,
  });
}

/**
 * Default `date_start` for scheduled Mailrail sends: now + 15 minutes, rounded up to the next
 * 5-minute boundary, formatted as an ISO date-time string. Rounds the full epoch timestamp so
 * seconds and milliseconds always round upward — rounding the minute field alone would truncate
 * them backwards past the 15-minute floor whenever the minute already sits on a boundary.
 */
export function getDefaultStartTimestamp(): string {
  const earliestStartMs = moment.utc().add(15, 'minutes').valueOf();
  const roundedStartMs = Math.ceil(earliestStartMs / FIVE_MINUTES_MS) * FIVE_MINUTES_MS;
  return moment.utc(roundedStartMs).format(TimeFormat.isoDateTime);
}
